import { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faArrowUpRightFromSquare,
  faCalendarDays,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faCode,
  faCodeBranch,
  faCodeCommit,
  faDownload,
  faEye,
  faFileLines,
  faFilter,
  faMagnifyingGlass,
  faRotate,
  faStopwatch,
  faTrash,
  faTriangleExclamation,
  faTrophy,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { getReports, getReportBlob, deleteReport, type SavedReport } from '@/services/storage';
import * as historyStore from '@/services/analysis-history-store';
import * as audit from '@/services/audit';
import { ANALYSIS_MODE_LABELS, ANALYSIS_MODE_COLORS, type AnalysisSnapshotMeta, type AnalysisMode } from '@/services/analysis-history-store';

type ReportMeta = Omit<SavedReport, 'pdfBlob'>;

interface AnalysisHistoryAuthor {
  name: string;
  email?: string;
  commits: number;
  additions: number;
  deletions: number;
  activeDays: number;
}

interface AnalysisHistoryBranch {
  name: string;
  isRemote: boolean;
  commitCount: number;
  lastAuthor?: string;
}

interface AnalysisHistory {
  id: string;
  timestamp?: string;
  createdAt?: string;
  repoName: string;
  repoType?: string;
  repoUrl?: string;
  stats?: { totalCommits: number; totalAuthors: number; totalBranches?: number; totalAdditions?: number; totalDeletions?: number };
  totalCommits?: number;
  totalAuthors?: number;
  dateRange?: { start: string; end: string };
  topAuthors?: AnalysisHistoryAuthor[];
  topBranches?: AnalysisHistoryBranch[];
  duration?: number;
  durationMs?: number;
  success: boolean;
  error?: string;
}

interface ReportsViewProps {
  inline?: boolean;
  onClose: () => void;
  history: AnalysisHistory[];
  onReopenSnapshot?: (snapshotId: string) => void;
}

const REPORT_TYPE_LABELS: Record<string, { label: string; color: string; darkColor: string }> = {
  main: { label: 'Git Analysis', color: 'bg-blue-100 text-blue-700 border-blue-200', darkColor: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  individual: { label: 'Individual', color: 'bg-purple-100 text-purple-700 border-purple-200', darkColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  'cross-project': { label: 'Cross-Project', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', darkColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  combined: { label: 'Combined', color: 'bg-orange-100 text-orange-700 border-orange-200', darkColor: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  jira: { label: 'Jira', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', darkColor: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  confluence: { label: 'Confluence', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', darkColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  slack: { label: 'Slack', color: 'bg-rose-100 text-rose-700 border-rose-200', darkColor: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
  teams: { label: 'Teams', color: 'bg-violet-100 text-violet-700 border-violet-200', darkColor: 'bg-violet-500/20 text-violet-300 border-violet-500/30' },
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function parseDate(item: AnalysisHistory): Date {
  return new Date(item.timestamp || item.createdAt || Date.now());
}

function toLocalDateStr(d: Date): string {
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export function ReportsView({ inline, onClose: _onClose, history, onReopenSnapshot }: ReportsViewProps) {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [activeTab, setActiveTab] = useState<'reports' | 'analyses' | 'snapshots'>('snapshots');
  const [reports, setReports] = useState<ReportMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState<Set<string>>(new Set());
  const [snapshots, setSnapshots] = useState<AnalysisSnapshotMeta[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(true);
  const [snapshotModeFilter, setSnapshotModeFilter] = useState<string>('all');

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReports();
      setReports(data);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSnapshots = useCallback(async () => {
    setSnapshotsLoading(true);
    try {
      const data = await historyStore.getAllSnapshotMeta();
      setSnapshots(data);
    } catch (err) {
      console.error('Failed to load snapshots:', err);
    } finally {
      setSnapshotsLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

  // Refresh data when component becomes visible (for lazy loading navigation)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadReports();
        loadSnapshots();
      }
    };

    const handleFocus = () => {
      loadReports();
      loadSnapshots();
    };

    const handleSnapshotSaved = () => loadSnapshots();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('verigit:snapshot-saved', handleSnapshotSaved);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('verigit:snapshot-saved', handleSnapshotSaved);
    };
  }, [loadReports, loadSnapshots]);

  const filteredSnapshots = useMemo(() => {
    let filtered = [...snapshots];
    if (snapshotModeFilter !== 'all') {
      filtered = filtered.filter(s => s.mode === snapshotModeFilter);
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      filtered = filtered.filter(s => s.title.toLowerCase().includes(q));
    }
    return filtered;
  }, [snapshots, snapshotModeFilter, filterSearch]);

  const groupedSnapshots = useMemo(() => {
    const groups = new Map<string, AnalysisSnapshotMeta[]>();
    for (const s of filteredSnapshots) {
      const date = toLocalDateStr(new Date(s.timestamp));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(s);
    }
    return Array.from(groups.entries());
  }, [filteredSnapshots]);

  const handleDeleteSnapshot = async (id: string) => {
    setDeletingId(id);
    try {
      audit.auditSnapshotDeleted(id);
      await historyStore.deleteSnapshot(id);
      setSnapshots(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete snapshot:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (report: ReportMeta) => {
    try {
      const blob = await getReportBlob(report.id);
      if (!blob) return;
      const url = URL.createObjectURL(new Blob([new Uint8Array(blob)], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = report.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download report:', err);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      audit.auditReportDownloaded(id);
      await deleteReport(id);
      setReports(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error('Failed to delete report:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleDate = (date: string) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  // Filter reports
  const filteredReports = useMemo(() => {
    let filtered = reports;
    if (filterType !== 'all') filtered = filtered.filter(r => r.type === filterType);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      filtered = filtered.filter(r => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || r.filename.toLowerCase().includes(q));
    }
    return filtered;
  }, [reports, filterType, filterSearch]);

  // Filter analyses
  const filteredHistory = useMemo(() => {
    let filtered = [...history];
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      filtered = filtered.filter(h => h.repoName.toLowerCase().includes(q));
    }
    if (filterType !== 'all') {
      filtered = filtered.filter(h => {
        const rt = (h.repoType || '').toLowerCase();
        if (filterType === 'main') return rt === 'github' || rt === 'bitbucket' || rt === 'local' || rt === '';
        return rt === filterType;
      });
    }
    return filtered.sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime());
  }, [history, filterSearch, filterType]);

  // Group reports by date
  const groupedReports = useMemo(() => {
    const groups = new Map<string, ReportMeta[]>();
    for (const r of filteredReports) {
      const date = toLocalDateStr(new Date(r.createdAt));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(r);
    }
    return Array.from(groups.entries());
  }, [filteredReports]);

  // Group analyses by date
  const groupedHistory = useMemo(() => {
    const groups = new Map<string, AnalysisHistory[]>();
    for (const h of filteredHistory) {
      const date = toLocalDateStr(parseDate(h));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(h);
    }
    return Array.from(groups.entries());
  }, [filteredHistory]);

  // Stats
  const reportStats = useMemo(() => {
    const totalSize = reports.reduce((sum, r) => sum + r.sizeBytes, 0);
    const typeCounts = new Map<string, number>();
    for (const r of reports) typeCounts.set(r.type, (typeCounts.get(r.type) || 0) + 1);
    return { total: reports.length, totalSize, typeCounts };
  }, [reports]);

  const analysisStats = useMemo(() => {
    // Combine old server history + new IndexedDB snapshots for stats
    const historySuccessful = history.filter(h => h.success).length;
    const historyFailed = history.filter(h => !h.success).length;
    const historyCommits = history.reduce((sum, h) => sum + (h.stats?.totalCommits || h.totalCommits || 0), 0);
    const snapshotCommits = snapshots.reduce((sum, s) => sum + (s.summary?.commitCount || 0), 0);

    const successful = historySuccessful + snapshots.filter(s => s.success).length;
    const failed = historyFailed + snapshots.filter(s => !s.success).length;
    const totalCommits = historyCommits + snapshotCommits;
    const total = history.length + snapshots.length;

    const totalAuthors = new Set(history.flatMap(h => {
      const count = h.stats?.totalAuthors || h.totalAuthors || 0;
      return Array.from({ length: count }, (_, i) => `${h.repoName}-author-${i}`);
    })).size;

    // Use snapshots for avg duration (they have accurate timing); fall back to history if no snapshots
    const snapshotsWithDuration = snapshots.filter(s => s.durationMs > 0);
    const historyWithDuration = history.filter(h => (h.durationMs ?? h.duration ?? 0) > 0);
    const allDurations = [
      ...snapshotsWithDuration.map(s => s.durationMs),
      ...historyWithDuration.map(h => h.durationMs ?? h.duration ?? 0),
    ];
    const avgDuration = allDurations.length > 0
      ? allDurations.reduce((sum, d) => sum + d, 0) / allDurations.length
      : 0;

    return { total, successful, failed, totalCommits, totalAuthors, avgDuration };
  }, [history, snapshots]);

  return (
    <div className={`${inline ? 'h-[calc(100vh-4rem)]' : 'fixed inset-0 z-[100]'} flex flex-col overflow-hidden font-sans`} style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>

      {/* Header */}
      <div className={`flex-shrink-0 z-10 bg-gray-50/0 dark:border-slate-700/50 dark:bg-slate-800/0 backdrop-blur-xl border-none ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faFileLines} className="h-5 w-5 text-blue-400" />
              <FontAwesomeIcon icon={faChartBar} className="h-4 w-4 text-emerald-400" />
            </div>
            <div>
              <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Reports & Analytics</h1>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Generated reports and analysis history</p>
            </div>
          </div>
          <button onClick={loadReports} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-black/[0.05]'}`}>
            <FontAwesomeIcon icon={faRotate} className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
        </div>

        {/* Summary cards */}
        <div className="max-w-7xl mx-auto px-6 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={`${isDark ? 'bg-white/[0.03]' : 'bg-white/60'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faFileLines} className={`h-4 w-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>PDF Reports</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{reportStats.total}</p>
              <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {formatBytes(reportStats.totalSize)} stored
              </p>
            </div>
            <div className={`${isDark ? 'bg-white/[0.03]' : 'bg-white/60'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faChartBar} className={`h-4 w-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Analyses Run</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analysisStats.total}</p>
              <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {analysisStats.successful} success · {analysisStats.failed} failed
              </p>
            </div>
            <div className={`${isDark ? 'bg-white/[0.03]' : 'bg-white/60'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faCodeCommit} className={`h-4 w-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Commits</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analysisStats.totalCommits.toLocaleString()}</p>
              <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>across all analyses</p>
            </div>
            <div className={`${isDark ? 'bg-white/[0.03]' : 'bg-white/60'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faStopwatch} className={`h-4 w-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Avg Duration</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatDuration(analysisStats.avgDuration)}</p>
              <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>per analysis</p>
            </div>
          </div>
        </div>

        {/* Tabs + filters */}
        <div className="max-w-7xl mx-auto px-6 pb-3 flex flex-wrap items-center gap-3">
          <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]'} backdrop-blur-sm`}>
            <button onClick={() => { setActiveTab('reports'); setFilterType('all'); }}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === 'reports'
                ? (isDark ? 'bg-white/[0.08] text-white' : 'bg-white/80 text-slate-900 shadow-sm')
                : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}`}>
              <FontAwesomeIcon icon={faFileLines} className="h-3 w-3 inline mr-1.5" />PDF Reports ({reports.length})
            </button>
            <button onClick={() => { setActiveTab('analyses'); setFilterType('all'); }}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === 'analyses'
                ? (isDark ? 'bg-white/[0.08] text-white' : 'bg-white/80 text-slate-900 shadow-sm')
                : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}`}>
              <FontAwesomeIcon icon={faChartBar} className="h-3 w-3 inline mr-1.5" />Analysis History ({history.length})
            </button>
            <button onClick={() => { setActiveTab('snapshots'); setFilterType('all'); setSnapshotModeFilter('all'); }}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === 'snapshots'
                ? (isDark ? 'bg-white/[0.08] text-white' : 'bg-white/80 text-slate-900 shadow-sm')
                : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}`}>
              <FontAwesomeIcon icon={faEye} className="h-3 w-3 inline mr-1.5" />Saved Analyses ({snapshots.length})
            </button>
          </div>

          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <FontAwesomeIcon icon={faMagnifyingGlass} className={`absolute left-2.5 top-2 h-3.5 w-3.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
              placeholder={activeTab === 'reports' ? 'Search reports...' : 'Search repos...'}
              className={`w-full pl-8 pr-3 py-1.5 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isDark ? 'bg-white/[0.04] border-white/[0.08] text-white placeholder-slate-500' : 'bg-white/80 border-black/[0.08] text-slate-900 placeholder-slate-400'}`} />
          </div>

          {activeTab === 'reports' && (
            <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]'} backdrop-blur-sm`}>
              <FontAwesomeIcon icon={faFilter} className={`h-3 w-3 my-auto mx-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              {['all', 'main', 'individual', 'cross-project', 'combined', 'jira'].map(t => (
                <button key={t} onClick={() => setFilterType(t)}
                  className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors capitalize ${filterType === t
                    ? (isDark ? 'bg-white/[0.08] text-white' : 'bg-white/80 text-slate-900 shadow-sm')
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}`}>
                  {t === 'all' ? 'All' : REPORT_TYPE_LABELS[t]?.label || t}
                </button>
              ))}
            </div>
          )}
          {activeTab === 'snapshots' && (
            <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]'} backdrop-blur-sm`}>
              <FontAwesomeIcon icon={faFilter} className={`h-3 w-3 my-auto mx-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              {(['all', 'combined', 'cross_project', 'jira', 'linear', 'trello', 'slack', 'teams'] as const).map(t => (
                <button key={t} onClick={() => setSnapshotModeFilter(t)}
                  className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${snapshotModeFilter === t
                    ? (isDark ? 'bg-white/[0.08] text-white' : 'bg-white/80 text-slate-900 shadow-sm')
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}`}>
                  {t === 'all' ? 'All' : ANALYSIS_MODE_LABELS[t as AnalysisMode]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-y-auto ${isDark ? '' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 space-y-4">

          {/* PDF Reports Tab */}
          {activeTab === 'reports' && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <FontAwesomeIcon icon={faRotate} className={`h-6 w-6 animate-spin ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="text-center py-20">
                  <FontAwesomeIcon icon={faFileLines} className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                  <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {reports.length === 0 ? 'No reports yet' : 'No reports match your filters'}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {reports.length === 0 ? 'PDF reports will appear here after you generate them' : 'Try adjusting your search or filter'}
                  </p>
                </div>
              ) : (
                groupedReports.map(([date, items]) => {
                  const isExpanded = !expandedDates.has(date);
                  return (
                    <div key={date}>
                      <button onClick={() => toggleDate(date)}
                        className={`flex items-center gap-2 mb-2 text-xs font-medium ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'} transition-colors`}>
                        {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />}
                        {date}
                        <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>({items.length})</span>
                      </button>
                      {isExpanded && (
                        <div className="space-y-2 ml-5">
                          {items.map(report => {
                            const typeInfo = REPORT_TYPE_LABELS[report.type] || { label: report.type, color: 'bg-gray-100 text-gray-700', darkColor: 'bg-gray-500/20 text-gray-300' };
                            const time = new Date(report.createdAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
                            return (
                              <div key={report.id}
                                className={`${isDark ? 'bg-white/[0.03] hover:bg-white/[0.05]' : 'bg-white/60 hover:bg-white/80'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl p-4 transition-colors`}>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/[0.08]' : 'bg-blue-50/80'}`}>
                                      <FontAwesomeIcon icon={faFileLines} className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 mb-0.5">
                                        <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{report.title}</span>
                                        <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded border ${isDark ? typeInfo.darkColor : typeInfo.color}`}>{typeInfo.label}</span>
                                      </div>
                                      <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{report.description}</p>
                                      <div className={`flex items-center gap-3 mt-1 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                        <span className="flex items-center gap-1"><FontAwesomeIcon icon={faClock} className="h-3 w-3" />{time}</span>
                                        <span>{formatBytes(report.sizeBytes)}</span>
                                        <span className="truncate opacity-60">{report.filename}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button onClick={() => handleDownload(report)}
                                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/[0.06] text-blue-400 hover:text-blue-300' : 'hover:bg-blue-50 text-blue-600 hover:text-blue-700'}`}
                                      title="Download">
                                      <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => handleDelete(report.id)}
                                      disabled={deletingId === report.id}
                                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/[0.06] text-red-400 hover:text-red-300' : 'hover:bg-red-50 text-red-500 hover:text-red-600'} disabled:opacity-50`}
                                      title="Delete">
                                      <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}

          {/* Analysis History Tab */}
          {activeTab === 'analyses' && (
            <>
              {filteredHistory.length === 0 ? (
                <div className="text-center py-20">
                  <FontAwesomeIcon icon={faChartBar} className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                  <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {history.length === 0 ? 'No analyses yet' : 'No analyses match your search'}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    Run an analysis to see results here
                  </p>
                </div>
              ) : (
                groupedHistory.map(([date, items]) => {
                  const isExpanded = !expandedDates.has(date);
                  return (
                    <div key={date}>
                      <button onClick={() => toggleDate(date)}
                        className={`flex items-center gap-2 mb-2 text-xs font-medium ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'} transition-colors`}>
                        {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />}
                        {date}
                        <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>({items.length})</span>
                      </button>
                      {isExpanded && (
                        <div className="space-y-2 ml-5">
                          {items.map(item => {
                            const dur = item.durationMs ?? item.duration ?? 0;
                            const time = parseDate(item).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
                            const commits = item.stats?.totalCommits || item.totalCommits || 0;
                            const authors = item.stats?.totalAuthors || item.totalAuthors || 0;
                            const branches = item.stats?.totalBranches || 0;
                            const additions = item.stats?.totalAdditions || 0;
                            const deletions = item.stats?.totalDeletions || 0;
                            const isItemExpanded = expandedAnalysis.has(item.id);
                            const hasDetails = item.topAuthors?.length || item.topBranches?.length || additions > 0 || branches > 0;
                            return (
                              <div key={item.id}
                                className={`${isDark ? 'bg-white/[0.03]' : 'bg-white/60'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl overflow-hidden transition-colors`}>
                                <button
                                  onClick={() => setExpandedAnalysis(prev => {
                                    const next = new Set(prev);
                                    next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                                    return next;
                                  })}
                                  className={`w-full text-left p-4 ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-white/80'} transition-colors`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                                        item.success
                                          ? (isDark ? 'bg-emerald-500/[0.08]' : 'bg-emerald-50/80')
                                          : (isDark ? 'bg-red-500/[0.08]' : 'bg-red-50/80')
                                      }`}>
                                        {item.success
                                          ? <FontAwesomeIcon icon={faCircleCheck} className={`h-5 w-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                                          : <FontAwesomeIcon icon={faCircleXmark} className={`h-5 w-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                                        }
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                          <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.repoName}</span>
                                          {item.repoType && (
                                            <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded border ${isDark ? 'bg-white/[0.05] text-slate-300 border-white/[0.08]' : 'bg-slate-100/80 text-slate-600 border-slate-200'}`}>{item.repoType}</span>
                                          )}
                                          <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded border ${
                                            item.success
                                              ? (isDark ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200')
                                              : (isDark ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-red-100 text-red-700 border-red-200')
                                          }`}>
                                            {item.success ? 'Success' : 'Failed'}
                                          </span>
                                        </div>
                                        <div className={`flex items-center gap-4 mt-1 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                          <span className="flex items-center gap-1"><FontAwesomeIcon icon={faClock} className="h-3 w-3" />{time}</span>
                                          <span className="flex items-center gap-1"><FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3" />{commits.toLocaleString()} commits</span>
                                          <span className="flex items-center gap-1"><FontAwesomeIcon icon={faUsers} className="h-3 w-3" />{authors} authors</span>
                                          {dur > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faStopwatch} className="h-3 w-3" />{formatDuration(dur)}</span>}
                                        </div>
                                        {item.error && (
                                          <div className={`flex items-center gap-1 mt-1.5 text-[10px] ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                                            <FontAwesomeIcon icon={faTriangleExclamation} className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">{item.error}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {hasDetails && (
                                        <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform ${isDark ? 'text-slate-500' : 'text-slate-400'} ${isItemExpanded ? 'rotate-180' : ''}`} />
                                      )}
                                    </div>
                                  </div>
                                </button>

                                {/* Expanded details */}
                                {isItemExpanded && (
                                  <div className={`border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} px-4 pb-4 pt-3 space-y-4`}>
                                    {/* Stats grid */}
                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                      <div className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/40 border border-black/[0.06]'}`}>
                                        <FontAwesomeIcon icon={faCodeCommit} className={`h-3.5 w-3.5 mx-auto mb-1 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                                        <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{commits.toLocaleString()}</div>
                                        <div className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Commits</div>
                                      </div>
                                      <div className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/40 border border-black/[0.06]'}`}>
                                        <FontAwesomeIcon icon={faUsers} className={`h-3.5 w-3.5 mx-auto mb-1 ${isDark ? 'text-violet-400' : 'text-violet-500'}`} />
                                        <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{authors}</div>
                                        <div className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Authors</div>
                                      </div>
                                      <div className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/40 border border-black/[0.06]'}`}>
                                        <FontAwesomeIcon icon={faCodeBranch} className={`h-3.5 w-3.5 mx-auto mb-1 ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`} />
                                        <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{branches}</div>
                                        <div className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Branches</div>
                                      </div>
                                      <div className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/40 border border-black/[0.06]'}`}>
                                        <FontAwesomeIcon icon={faArrowTrendUp} className={`h-3.5 w-3.5 mx-auto mb-1 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
                                        <div className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>+{additions.toLocaleString()}</div>
                                        <div className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Additions</div>
                                      </div>
                                      <div className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/40 border border-black/[0.06]'}`}>
                                        <FontAwesomeIcon icon={faArrowTrendDown} className={`h-3.5 w-3.5 mx-auto mb-1 ${isDark ? 'text-rose-400' : 'text-rose-500'}`} />
                                        <div className={`text-lg font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>-{deletions.toLocaleString()}</div>
                                        <div className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Deletions</div>
                                      </div>
                                      <div className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/40 border border-black/[0.06]'}`}>
                                        <FontAwesomeIcon icon={faCalendarDays} className={`h-3.5 w-3.5 mx-auto mb-1 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                                        <div className={`text-[10px] font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                          {item.dateRange ? (
                                            <>{new Date(item.dateRange.start).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} – {new Date(item.dateRange.end).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</>
                                          ) : '—'}
                                        </div>
                                        <div className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Period</div>
                                      </div>
                                    </div>

                                    {/* Top Contributors */}
                                    {item.topAuthors && item.topAuthors.length > 0 && (
                                      <div>
                                        <div className={`flex items-center gap-2 mb-2 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                          <FontAwesomeIcon icon={faTrophy} className="h-3.5 w-3.5 text-amber-500" />
                                          Contributors ({item.topAuthors.length})
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {item.topAuthors.sort((a, b) => b.commits - a.commits).map((author, aIdx) => (
                                            <div key={author.name}
                                              className={`flex items-center justify-between p-2.5 rounded-lg ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-white/40 border border-black/[0.04]'}`}>
                                              <div className="flex items-center gap-2 min-w-0">
                                                <span className={`text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold ${
                                                  aIdx === 0 ? (isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700') :
                                                  aIdx === 1 ? (isDark ? 'bg-slate-400/20 text-slate-300' : 'bg-slate-200 text-slate-600') :
                                                  aIdx === 2 ? (isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700') :
                                                  (isDark ? 'bg-white/[0.06] text-slate-400' : 'bg-slate-100 text-slate-500')
                                                }`}>
                                                  {aIdx + 1}
                                                </span>
                                                <div className="min-w-0">
                                                  <div className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{author.name}</div>
                                                  {author.email && (
                                                    <div className={`text-[9px] truncate ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{author.email}</div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-3 text-[10px] shrink-0">
                                                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                                                  {author.commits} commits
                                                </span>
                                                <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>+{author.additions.toLocaleString()}</span>
                                                <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>-{author.deletions.toLocaleString()}</span>
                                                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>
                                                  {author.activeDays}d
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Top Branches */}
                                    {item.topBranches && item.topBranches.length > 0 && (
                                      <div>
                                        <div className={`flex items-center gap-2 mb-2 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                          <FontAwesomeIcon icon={faCodeBranch} className="h-3.5 w-3.5 text-indigo-500" />
                                          Branches ({item.topBranches.length})
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                          {item.topBranches.sort((a, b) => b.commitCount - a.commitCount).slice(0, 12).map(branch => (
                                            <span key={branch.name}
                                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium ${
                                                branch.isRemote
                                                  ? (isDark ? 'bg-blue-500/10 text-blue-300 border border-blue-500/20' : 'bg-blue-50 text-blue-700 border border-blue-200')
                                                  : (isDark ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20' : 'bg-purple-50 text-purple-700 border border-purple-200')
                                              }`}>
                                              <FontAwesomeIcon icon={faCodeBranch} className="h-3 w-3" />
                                              {branch.name.replace(/^origin\//, '')}
                                              <span className="opacity-60">({branch.commitCount})</span>
                                            </span>
                                          ))}
                                          {item.topBranches.length > 12 && (
                                            <span className={`inline-flex items-center px-2 py-1 rounded-lg text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                              +{item.topBranches.length - 12} more
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* No detailed data message for older entries */}
                                    {!item.topAuthors?.length && !item.topBranches?.length && additions === 0 && branches === 0 && (
                                      <div className={`text-center py-3 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                        <FontAwesomeIcon icon={faCode} className="h-4 w-4 mx-auto mb-1 opacity-50" />
                                        Detailed snapshot data not available for this analysis.
                                        <br />
                                        <span className="text-[10px]">Run a new analysis to capture contributor and branch details.</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}

          {/* Saved Analyses (Snapshots) Tab */}
          {activeTab === 'snapshots' && (
            <>
              {snapshotsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <FontAwesomeIcon icon={faRotate} className={`h-6 w-6 animate-spin ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                </div>
              ) : filteredSnapshots.length === 0 ? (
                <div className="text-center py-20">
                  <FontAwesomeIcon icon={faEye} className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                  <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {snapshots.length === 0 ? 'No saved analyses yet' : 'No analyses match your filters'}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    Run a Combined, Cross-Project, Jira, Linear, or Trello analysis to save it here
                  </p>
                </div>
              ) : (
                groupedSnapshots.map(([date, items]) => {
                  const isExpanded = !expandedDates.has(`snap-${date}`);
                  return (
                    <div key={`snap-${date}`}>
                      <button onClick={() => toggleDate(`snap-${date}`)}
                        className={`flex items-center gap-2 mb-2 text-xs font-medium ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'} transition-colors`}>
                        {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />}
                        {date}
                        <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>({items.length})</span>
                      </button>
                      {isExpanded && (
                        <div className="space-y-2 ml-5">
                          {items.map(snap => {
                            const time = new Date(snap.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
                            const modeColors = ANALYSIS_MODE_COLORS[snap.mode];
                            const modeLabel = ANALYSIS_MODE_LABELS[snap.mode];
                            return (
                              <div key={snap.id}
                                className={`${isDark ? 'bg-white/[0.03]' : 'bg-white/60'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl overflow-hidden transition-colors`}>
                                <div className={`p-4 ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-white/80'} transition-colors`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                                        snap.success
                                          ? (isDark ? 'bg-emerald-500/[0.08]' : 'bg-emerald-50/80')
                                          : (isDark ? 'bg-red-500/[0.08]' : 'bg-red-50/80')
                                      }`}>
                                        {snap.success
                                          ? <FontAwesomeIcon icon={faCircleCheck} className={`h-5 w-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                                          : <FontAwesomeIcon icon={faCircleXmark} className={`h-5 w-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                                        }
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                          <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{snap.title}</span>
                                          <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded border ${isDark ? modeColors.dark : modeColors.light}`}>
                                            {modeLabel}
                                          </span>
                                        </div>
                                        <div className={`flex items-center gap-3 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                          <span className="flex items-center gap-1">
                                            <FontAwesomeIcon icon={faClock} className="h-3 w-3" />{time}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <FontAwesomeIcon icon={faStopwatch} className="h-3 w-3" />{formatDuration(snap.durationMs)}
                                          </span>
                                          {snap.summary.commitCount > 0 && (
                                            <span className="flex items-center gap-1">
                                              <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3" />{snap.summary.commitCount.toLocaleString()} commits
                                            </span>
                                          )}
                                          {snap.summary.eventCount > 0 && (
                                            <span className="flex items-center gap-1">
                                              <FontAwesomeIcon icon={faChartBar} className="h-3 w-3" />{snap.summary.eventCount.toLocaleString()} events
                                            </span>
                                          )}
                                          {snap.summary.repoCount && snap.summary.repoCount > 0 && (
                                            <span className="flex items-center gap-1">
                                              <FontAwesomeIcon icon={faCodeBranch} className="h-3 w-3" />{snap.summary.repoCount} repos
                                            </span>
                                          )}
                                          {snap.summary.dateRange && (
                                            <span className="flex items-center gap-1">
                                              <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3" />
                                              {new Date(snap.summary.dateRange.start).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                                              {' — '}
                                              {new Date(snap.summary.dateRange.end).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      {onReopenSnapshot && snap.success && (
                                        <button
                                          onClick={() => { audit.auditSnapshotOpened(snap.id, snap.mode); onReopenSnapshot(snap.id); }}
                                          className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors flex items-center gap-1.5 ${
                                            isDark
                                              ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30'
                                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200'
                                          }`}>
                                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
                                          Reopen
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteSnapshot(snap.id)}
                                        disabled={deletingId === snap.id}
                                        className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-red-500/10 text-slate-500 hover:text-red-400' : 'hover:bg-red-50 text-slate-400 hover:text-red-600'}`}>
                                        {deletingId === snap.id ? <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
